import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, CheckSquare, FileText, Users, Building2, LogOut,
  Menu, X, ChevronLeft, ChevronRight, BarChart3, Settings, Sun, Moon,
  Workflow, Package, ClipboardCheck, Warehouse, ShoppingCart, IndianRupee, Truck, Bike, FileSpreadsheet, Upload, MailCheck, CalendarDays, Megaphone, Palette, TrendingDown
} from 'lucide-react';
import { taskMe } from '../../services/taskApi';
import { can } from './modules';
import { NotificationBell } from '../../components/NotificationBell';
import { toast } from 'sonner';

const TaskLayout = () => {
  const [user, setUser] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('task_theme') || 'dark');
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('task_theme', next);
  };

  useEffect(() => {
    const check = async () => {
      try {
        const res = await taskMe();
        setUser(res.data);
      } catch {
        localStorage.removeItem('task_token');
        localStorage.removeItem('task_user');
        navigate('/tasks');
      }
    };
    check();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('task_token');
    localStorage.removeItem('task_user');
    toast.success('Logged out');
    navigate('/tasks');
  };

  const navItems = [
    { path: '/tasks/dashboard', icon: LayoutDashboard, label: 'Dashboard', show: true },
    { path: '/tasks/board', icon: CheckSquare, label: 'Tasks', show: can(user, 'tasks') },
    { path: '/tasks/quotations', icon: FileText, label: 'Quotations', show: can(user, 'quotations') },
    { path: '/tasks/reports', icon: BarChart3, label: 'Reports', show: can(user, 'reports') },
    { type: 'divider', label: 'Flow Management', show: true },
    { path: '/tasks/flow', icon: Workflow, label: 'Flow Dashboard', show: true },
    { path: '/tasks/flow/sales-orders', icon: Package, label: 'Sales Orders', show: true },
    { path: '/tasks/flow/approvals', icon: ClipboardCheck, label: 'Approvals', show: user?.role === 'admin' },
    { path: '/tasks/flow/store', icon: Warehouse, label: 'Store', show: can(user, 'store') },
    { path: '/tasks/flow/billing', icon: IndianRupee, label: 'Billing', show: can(user, 'billing') },
    { path: '/tasks/flow/purchase', icon: ShoppingCart, label: 'Purchase', show: can(user, 'purchase') },
    { path: '/tasks/flow/logistics', icon: Truck, label: 'Logistics', show: can(user, 'logistics') },
    { path: '/tasks/flow/delivery-persons', icon: Bike, label: 'Delivery Persons', show: can(user, 'logistics') },
    { path: '/tasks/flow/stock-report', icon: FileSpreadsheet, label: 'Stock Report', show: true },
    { path: '/tasks/flow/stock-upload', icon: Upload, label: 'Stock Upload', show: can(user, 'store') },
    { path: '/tasks/flow/pendency', icon: TrendingDown, label: 'Pendency Report', show: can(user, 'pendency') },
    { path: '/tasks/flow/analytics', icon: BarChart3, label: 'Sales Analytics', show: can(user, 'analytics') },
    { type: 'divider', label: 'Marketing', show: user?.role === 'admin' || user?.role === 'marketing' },
    { path: '/tasks/marketing', icon: Megaphone, label: 'Marketing', show: user?.role === 'admin' || user?.role === 'marketing' },
    { type: 'divider', label: 'Admin', show: user?.role === 'admin' },
    { path: '/tasks/users', icon: Users, label: 'Team', show: user?.role === 'admin' },
    { path: '/tasks/firms', icon: Building2, label: 'Firms', show: user?.role === 'admin' },
    { path: '/tasks/flow/email-diagnostics', icon: MailCheck, label: 'Email Logs', show: user?.role === 'admin' },
    { path: '/tasks/flow/holidays', icon: CalendarDays, label: 'Holidays', show: user?.role === 'admin' },
    { path: '/tasks/flow/branding', icon: Palette, label: 'Email Branding', show: user?.role === 'admin' },
    { path: '/tasks/settings', icon: Settings, label: 'Settings', show: user?.role === 'admin' },
  ].filter(i => i.show);

  if (!user) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#080d1a]' : 'bg-gray-50'}`}>
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const SidebarContent = () => (
    <>
      <div className={`p-4 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-lg shadow-blue-500/20">
            NT
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <h2 className={`font-bold text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>Task Manager</h2>
              <p className={`text-[10px] ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Namoh Toolings</p>
            </div>
          )}
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {navItems.map((item, idx) => {
          if (item.type === 'divider') {
            return !collapsed ? (
              <div key={`div-${idx}`} className={`pt-3 pb-1.5 px-3 text-[9px] font-bold uppercase tracking-widest ${isDark ? 'text-white/20' : 'text-gray-300'}`}>
                {item.label}
              </div>
            ) : <div key={`div-${idx}`} className={`my-2 mx-3 border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`} />;
          }
          return (
            <NavLink
              key={item.path} to={item.path} end={item.path === '/tasks/flow'}
              onClick={() => setMobileOpen(false)}
              data-testid={`task-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? isDark ? 'bg-blue-500/15 text-blue-400 shadow-sm shadow-blue-500/5' : 'bg-blue-50 text-blue-600 shadow-sm'
                    : isDark ? 'text-white/40 hover:text-white/70 hover:bg-white/[0.03]' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`
              }
            >
              <item.icon size={18} className="flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>
      <div className={`p-3 border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
        <button
          onClick={toggleTheme}
          data-testid="theme-toggle-btn"
          className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm w-full mb-1 transition-all ${
            isDark ? 'text-amber-400/70 hover:text-amber-400 hover:bg-amber-500/10' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          } ${collapsed ? 'justify-center' : ''}`}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
          {!collapsed && <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>}
        </button>
        <div className={`flex items-center gap-3 px-3 py-2 ${collapsed ? 'justify-center' : ''}`}>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
            isDark ? 'bg-gradient-to-br from-blue-500/20 to-indigo-500/20 text-blue-400 border border-blue-500/10' : 'bg-blue-100 text-blue-600'
          }`}>
            {user.name?.charAt(0).toUpperCase()}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.name}</p>
              <p className={`text-[10px] capitalize ${isDark ? 'text-white/25' : 'text-gray-400'}`}>{user.role}</p>
            </div>
          )}
        </div>
        <button onClick={handleLogout} data-testid="task-logout-btn"
          className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm w-full mt-1 transition-all ${collapsed ? 'justify-center' : ''} ${
            isDark ? 'text-red-400/70 hover:text-red-400 hover:bg-red-500/10' : 'text-red-500 hover:text-red-600 hover:bg-red-50'
          }`}>
          <LogOut size={18} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className={`min-h-screen flex ${isDark ? 'bg-[#080d1a]' : 'bg-gray-50'}`}>
      <aside className={`hidden lg:flex flex-col fixed h-screen z-40 transition-all ${collapsed ? 'w-[68px]' : 'w-56'} ${
        isDark ? 'bg-[#0c1222] border-r border-white/5' : 'bg-white border-r border-gray-200 shadow-sm'
      }`}>
        <SidebarContent />
        <button onClick={() => setCollapsed(!collapsed)}
          className={`absolute -right-3 top-20 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
            isDark ? 'bg-[#0c1222] border border-white/10 text-white/50 hover:text-white' : 'bg-white border border-gray-300 text-gray-400 hover:text-gray-600 shadow-sm'
          }`}>
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>

      <div className={`lg:hidden fixed top-0 left-0 right-0 h-14 z-50 flex items-center px-4 gap-3 ${
        isDark ? 'bg-[#0c1222] border-b border-white/5' : 'bg-white border-b border-gray-200 shadow-sm'
      }`}>
        <button onClick={() => setMobileOpen(true)} className={isDark ? 'text-white' : 'text-gray-700'}><Menu size={22} /></button>
        <span className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Task Manager</span>
        <button onClick={toggleTheme} className={`ml-auto p-1.5 rounded-lg ${isDark ? 'text-amber-400/70 hover:bg-white/5' : 'text-gray-500 hover:bg-gray-100'}`} data-testid="theme-toggle-mobile">
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className={`absolute left-0 top-0 bottom-0 w-60 flex flex-col ${isDark ? 'bg-[#0c1222]' : 'bg-white'}`}>
            <div className="flex justify-end p-2">
              <button onClick={() => setMobileOpen(false)} className={`p-1 ${isDark ? 'text-white/40 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}><X size={20} /></button>
            </div>
            <SidebarContent />
          </aside>
        </div>
      )}

      <main className={`flex-1 transition-all ${collapsed ? 'lg:ml-[68px]' : 'lg:ml-56'} pt-14 lg:pt-0`}>
        <div className="p-4 lg:p-6 min-h-screen">
          <Outlet context={{ user, isDark, toggleTheme }} />
        </div>
      </main>
      <NotificationBell isDark={isDark} />
    </div>
  );
};

export default TaskLayout;
